import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { MessageSquare, Handshake, TrendingUp, Check } from "lucide-react";
import { toast } from "sonner";
import Seo from "../../components/Seo";
import api, { apiErr } from "../../lib/api";

const CATEGORIES = [
  { v: "feedback", icon: MessageSquare, title: "Feedback", desc: "Product feedback, suggestions or general comments." },
  { v: "partnership", icon: Handshake, title: "Partnership", desc: "Business, technology, distribution or strategic collaboration." },
  { v: "investment", icon: TrendingUp, title: "Investment", desc: "Investment or fundraising-related enquiries." },
];

export default function Contact() {
  const [params] = useSearchParams();
  const preset = params.get("category");
  const initialCat = CATEGORIES.some((c) => c.v === preset) ? preset : "";
  const [form, setForm] = useState({ category: initialCat, name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const er = {};
    if (!form.category) er.category = "Please choose what your message is about.";
    if (!form.name.trim()) er.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Please enter a valid email address.";
    if (!form.message.trim()) er.message = "Please enter a message.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Thanks for reaching out. We've received your message and will get back to you.");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto px-5 py-14">
      <Seo title="Contact" description="Have feedback, want to work with us, or interested in COMMITMENTT? Choose a category and send us a message." />
      <span className="text-xs tracking-[0.2em] uppercase font-semibold text-mint">Contact</span>
      <h1 className="font-heading text-4xl font-light text-ink mt-3">We'd love to hear from you.</h1>
      <p className="text-slatey mt-3">Have feedback, want to work with us, or interested in COMMITMENTT? Choose a category below and send us a message.</p>

      <Card className="rounded-2xl border-border shadow-card-sm p-6 mt-8">
        {sent ? (
          <div className="text-center py-8" data-testid="contact-success">
            <div className="h-12 w-12 rounded-full bg-calm-successbg flex items-center justify-center mx-auto"><Check size={22} className="text-calm-success" /></div>
            <p className="font-heading text-lg text-ink mt-4">Thanks for reaching out.</p>
            <p className="text-sm text-slatey mt-1">We've received your message and will get back to you.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" noValidate>
            <div>
              <Label className="text-ink">What would you like to contact us about?</Label>
              <div className="grid gap-2.5 mt-2" data-testid="contact-categories">
                {CATEGORIES.map((c) => {
                  const active = form.category === c.v;
                  return (
                    <button key={c.v} type="button" onClick={() => { setForm((f) => ({ ...f, category: c.v })); setErrors((er) => ({ ...er, category: undefined })); }}
                      aria-pressed={active} data-testid={`contact-cat-${c.v}`}
                      className={`flex items-start gap-3 text-left rounded-xl border p-3.5 transition-colors ${active ? "border-mint bg-mint-light/50 ring-1 ring-mint" : "border-border hover:border-mint/50 bg-white"}`}>
                      <span className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-mint text-white" : "bg-secondary text-navy"}`}><c.icon size={16} strokeWidth={1.5} /></span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-ink flex items-center gap-1.5">{c.title}{active && <Check size={14} className="text-mint" />}</span>
                        <span className="block text-xs text-slatey mt-0.5">{c.desc}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              {errors.category && <p className="text-xs text-calm-alert mt-1.5" data-testid="err-category">{errors.category}</p>}
            </div>

            <div>
              <Label htmlFor="contact-name" className="text-ink">Name</Label>
              <Input id="contact-name" name="name" autoComplete="name" value={form.name} onChange={set("name")} className="mt-1.5 rounded-xl" data-testid="contact-name" />
              {errors.name && <p className="text-xs text-calm-alert mt-1" data-testid="err-name">{errors.name}</p>}
            </div>
            <div>
              <Label htmlFor="contact-email" className="text-ink">Email</Label>
              <Input id="contact-email" name="email" type="email" autoComplete="email" value={form.email} onChange={set("email")} className="mt-1.5 rounded-xl" data-testid="contact-email" />
              {errors.email && <p className="text-xs text-calm-alert mt-1" data-testid="err-email">{errors.email}</p>}
            </div>
            <div>
              <Label htmlFor="contact-message" className="text-ink">Message</Label>
              <Textarea id="contact-message" name="message" rows={4} value={form.message} onChange={set("message")} className="mt-1.5 rounded-xl" data-testid="contact-message" />
              {errors.message && <p className="text-xs text-calm-alert mt-1" data-testid="err-message">{errors.message}</p>}
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-navy text-white hover:bg-navy-light h-11" data-testid="contact-submit">
              {loading ? "Sending…" : "Send message"}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
